import { Analytics } from './analytics'
import { FeedbackQuestionsEnum, FeedbackRatingEnum, FeedbackTypeEnum, feedbackQuestionsSchema } from '@gap/backend/app-services/feedback/models/FeedbackTypeEnum'

export enum FeedbackAnalyticsEvent {
   transcriptionSummaryFeedback = 'Transcription summary feedback',
   transcriptionSummaryImprovement = 'Transcription summary feedback improvement',
   feedbackSubmitted = 'Feedback submitted',
   meetingDeliverableFeedback = 'Meeting deliverable feedback',
   meetingDeliverableImprovement = 'Meeting deliverable feedback improvement',
}

export default class FeedbackAnalytics implements Analytics {
   private static readonly appLocationMap: Partial<Record<FeedbackTypeEnum, string>> = {
      'feedback-from-app-bar': 'Help menu',
      'feedback-from-meeting-workspace-banner': 'Meeting workspace banner',
   } as const

   static feedback = (params: { feedback: FeedbackRatingEnum, type: FeedbackTypeEnum }) => {
      const event = FeedbackAnalytics.getEvent(params.type).feedback

      if (event) {
         return Analytics.emitEvent(event, {
            'Feedback': params.feedback,
         })
      }
   }

   static improvement = (params: { type: FeedbackTypeEnum } & Record<string, string>) => {
      const event = FeedbackAnalytics.getEvent(params.type).improvement
      const questions = feedbackQuestionsSchema[params.type]

      const eventProperties = questions.reduce((acc, questionKey) => {
         const fieldValue = params[questionKey]
         const propertyLabel = FeedbackQuestionsEnum[questionKey]
         acc[propertyLabel] = fieldValue
         return acc
      }, {} as Record<string, string>)

      const appLocation = FeedbackAnalytics.getAppLocation(params.type)
      if (appLocation) {
         eventProperties['App location'] = appLocation
      }

      return Analytics.emitEvent(event, eventProperties)
   }

   private static getEvent = (type: FeedbackTypeEnum) => {
      switch (type) {
         case 'feedback-document-summary':
         case 'prework-document-summary':
            return {
               improvement: FeedbackAnalyticsEvent.transcriptionSummaryImprovement,
               feedback: FeedbackAnalyticsEvent.transcriptionSummaryFeedback,
            }
         case 'feedback-from-app-bar':
         case 'feedback-from-meeting-workspace-banner':
            return {
               improvement: FeedbackAnalyticsEvent.feedbackSubmitted,
            }
         case 'meeting-deliverable':
            return {
               improvement: FeedbackAnalyticsEvent.meetingDeliverableImprovement,
               feedback: FeedbackAnalyticsEvent.meetingDeliverableFeedback,
            }
         default:
            throw new Error(`Invalid feedback type: ${type}`)
      }
   }

   private static getAppLocation = (type: FeedbackTypeEnum): string | undefined => {
      return FeedbackAnalytics.appLocationMap[type]
   }
}
